import { Bike, Radio, ShoppingBag, Wallet } from "lucide-react";
import { formatCurrency } from "@veorest/utils";
import { StatsStrip } from "../data/StatsStrip";
import {
  partnerDriverPresence,
  type PartnerDriverRow,
} from "../../lib/partnerDrivers";
import type { PartnerOrderRow } from "../../lib/partnerOrders";
import type { PartnerWalletV2 } from "../../lib/partnerWallets";
import { useI18n } from "../../i18n";

interface DispatchIntelligenceProps {
  drivers: PartnerDriverRow[];
  orders: PartnerOrderRow[];
  fallbackEnabled: boolean;
  wallet: PartnerWalletV2 | null;
  loading?: boolean;
}

/** Read-only dispatch capacity snapshot — existing partner data only. */
export function DispatchIntelligence({
  drivers,
  orders,
  fallbackEnabled,
  wallet,
  loading,
}: DispatchIntelligenceProps) {
  const { t, locale } = useI18n();
  let online = 0;
  let busy = 0;
  let offline = 0;
  for (const d of drivers) {
    if (d.is_active === false) continue;
    const p = partnerDriverPresence(d);
    if (p === "online") online += 1;
    else if (p === "busy") busy += 1;
    else offline += 1;
  }

  const activeStages = new Set([
    "pending",
    "processing",
    "ready",
    "in_delivery",
  ]);
  const activeOrders = orders.filter((o) => activeStages.has(o.stage));
  const inDelivery = orders.filter((o) => o.stage === "in_delivery").length;
  const unassigned = activeOrders.filter(
    (o) =>
      o.fulfillment_type === "delivery" &&
      !o.assigned_driver_id &&
      o.stage !== "pending"
  ).length;

  const walletBalance = wallet?.balance ?? 0;
  const walletLimit = wallet?.negative_credit_limit ?? 0;
  const walletThreshold = wallet?.alert_threshold_iqd ?? 10000;
  const walletBlocked = wallet != null && walletBalance < walletLimit;
  const walletLow =
    wallet != null && !walletBlocked && walletBalance <= walletThreshold;
  const walletCaption =
    wallet == null
      ? t("common.dash")
      : walletBlocked
        ? t("overview.dispatch.walletBlocked")
        : walletLow
          ? t("overview.dispatch.walletLow")
          : t("overview.dispatch.walletCaption", {
              limit: formatCurrency(walletLimit, wallet.currency || "IQD", locale),
            });

  return (
    <section aria-label={t("overview.dispatch.aria")}>
      <StatsStrip
        loading={loading}
        tiles={[
          {
            key: "drivers",
            label: t("overview.dispatch.driverStatus"),
            value: online,
            icon: Bike,
            tone: "success",
            caption: t("overview.dispatch.driverCaption", {
              online,
              busy,
              offline,
            }),
          },
          {
            key: "load",
            label: t("overview.dispatch.activeLoad"),
            value: activeOrders.length,
            icon: ShoppingBag,
            tone: "accent",
            caption: t("overview.dispatch.loadCaption", {
              inDelivery,
              unassigned,
            }),
          },
          {
            key: "wallet",
            label: t("overview.dispatch.wallet"),
            value: wallet
              ? formatCurrency(walletBalance, wallet.currency || "IQD", locale)
              : "—",
            icon: Wallet,
            tone: "ink",
            caption: walletCaption,
          },
          {
            key: "fallback",
            label: t("overview.dispatch.fallback"),
            value: fallbackEnabled ? t("common.enabled") : t("common.disabled"),
            icon: Radio,
            tone: fallbackEnabled ? "info" : "ink",
            caption: t("overview.dispatch.fallbackCaption"),
          },
        ]}
      />
    </section>
  );
}
